import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import {
  OtherReportActionButtons,
  OtherReportDocument,
  OtherReportEmpty,
  OtherReportEmptyCells,
  OtherReportFilterCard,
  OtherReportLoader,
  OtherReportMultiSelectField,
  OtherReportPageHeader,
  OtherReportPlaceholder,
  OtherReportResultsZone,
  OtherReportShell,
  OtherReportSummaryGrid,
  OtherReportTable,
  OtherReportToolbar,
  ReportDateRangeFields,
} from './other-reports/OtherReportShared';
import './other-reports/report-table.css';

const CUSTOMER_STATEMENT_COLUMNS = [
  { key: 'date', label: 'Date', colClass: 'cs-date' },
  { key: 'particulars', label: 'Particulars', colClass: 'cs-particulars' },
  { key: 'intent', label: 'Indent', colClass: 'cs-indent' },
  { key: 'vehicle', label: 'Vehicle', colClass: 'cs-vehicle' },
  { key: 'product', label: 'Product', colClass: 'cs-product' },
  { key: 'description', label: 'Description', colClass: 'cs-description' },
  { key: 'quantity', label: 'Quantity', align: 'right', colClass: 'cs-qty' },
  { key: 'price', label: 'Price', align: 'right', colClass: 'cs-price' },
  { key: 'debit', label: 'Debit', align: 'right', colClass: 'cs-debit' },
  { key: 'credit', label: 'Credit', align: 'right', colClass: 'cs-credit' },
  { key: 'outstanding', label: 'Outstanding', align: 'right', colClass: 'cs-outstanding' },
];

const STATEMENT_PARTICULARS = {
  opening_balance: 'Opening balance',
  credit_sales: 'Credit sales',
  receipt_voucher: 'Receipt voucher',
  payment_voucher: 'Payment voucher',
  account_transfer: 'Account transfer',
  debit_note: 'Debit note',
  credit_note: 'Credit note',
};

const formatStatementParticulars = (value) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
  if (STATEMENT_PARTICULARS[key]) {
    return STATEMENT_PARTICULARS[key];
  }
  if (raw.includes('_')) {
    return raw.replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase());
  }
  return raw;
};

const CustomerStatementReport = (props) => {
  const [meta, setMeta] = useState({ customers: [] });
  const [inputValues, setInputValues] = useState({
    customer_ids: [],
    start_date: '',
    end_date: '',
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/customer_statement`)
      .then(setMeta)
      .catch((error) => swal('Customer Statement', error.message, 'error'));
  }, []);

  const customerOptions = useMemo(() => {
    const options = (meta.customers || []).map((customer) => ({
      value: customer.id,
      label: customer.name,
    }));
    if (meta.new_customer_head_id) {
      options.push({
        value: meta.new_customer_head_id,
        label: 'New Customer',
      });
    }
    return options;
  }, [meta]);

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    all_cust: inputValues.customer_ids || [],
    customer_name: (inputValues.customer_ids || [])[0] || '',
  });

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    if (!inputValues.customer_ids?.length) {
      swal('Please select at least one customer', '', 'warning');
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);
    try {
      const data = await fetchReportJson(`${API_URL}/customer_statement`, buildPayload());
      setReport(data);
    } catch (error) {
      swal('Customer Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/customer_statement/pdf`,
        buildPayload(),
        'customer_statement.pdf',
      );
    } catch (error) {
      swal('Customer Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const sections = useMemo(() => {
    if (!report) {
      return [];
    }
    if (Array.isArray(report.sections) && report.sections.length) {
      return report.sections;
    }
    return [{
      account_name: report.account_name,
      opening_bal: report.opening_bal || [],
      customer_statement: report.customer_statement || [],
      total_dr: report.total_dr,
      total_cr: report.total_cr,
      closing_balance: report.closing_balance,
      cr_dr: report.cr_dr,
    }];
  }, [report]);

  const selectedCustomerCount = inputValues.customer_ids?.length || 0;
  const totalRows = useMemo(
    () => sections.reduce((sum, section) => {
      const opening = (section.opening_bal || []).filter((row) => Number(row.outstanding || 0) !== 0).length;
      return sum + opening + (section.customer_statement || []).length;
    }, 0),
    [sections]
  );
  const hasLedgerRows = totalRows > 0;

  const summaryItems = useMemo(() => {
    if (!report || sections.length !== 1) {
      return [];
    }
    const section = sections[0];
    return [
      { label: 'Total debit', value: reportNumberFormat(section.total_dr) },
      { label: 'Total credit', value: reportNumberFormat(section.total_cr) },
      {
        label: 'Closing balance',
        value: `${reportNumberFormat(section.closing_balance)}${section.cr_dr || ''}`,
      },
    ];
  }, [report, sections]);

  const renderSectionRows = (section) => {
    const openingRows = (section.opening_bal || []).filter((row) => {
      const outstanding = Number(row.outstanding || 0);
      return outstanding !== 0;
    });
    const statementRows = section.customer_statement || [];

    return (
      <>
        {openingRows.map((row, index) => (
          <tr key={`opening-${section.customer_id || section.account_name}-${index}`} className="other-report-opening-row">
            <td>{formatReportDate(row.ac_date || report.start_date)}</td>
            <td>{formatStatementParticulars(row.particulars) || 'Opening balance'}</td>
            <OtherReportEmptyCells count={6} />
            <td className="report-amount">{reportNumberFormat(row.dr_amount)}</td>
            <td className="report-amount">{reportNumberFormat(row.cr_amount)}</td>
            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
          </tr>
        ))}
        {statementRows.map((row, index) => (
          <tr key={`row-${section.customer_id || section.account_name}-${index}`}>
            <td>{formatReportDate(row.ac_date || row.op_date)}</td>
            <td>{formatStatementParticulars(row.particulars)}</td>
            <td>{row.intent_no || ''}</td>
            <td>{row.vehicle_no || ''}</td>
            <td>{row.fuel_name || row.product || ''}</td>
            <td>{row.description || ''}</td>
            <td className="report-amount">{reportNumberFormat(row.quantity)}</td>
            <td className="report-amount">{reportNumberFormat(row.price)}</td>
            <td className="report-amount">{reportNumberFormat(row.dr_amount)}</td>
            <td className="report-amount">{reportNumberFormat(row.cr_amount)}</td>
            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
          </tr>
        ))}
        <tr className="other-report-total-row">
          <td colSpan="8" className="text-end">
            {(section.account_name || 'Customer')} — Total / Closing balance
          </td>
          <td className="report-amount">{reportNumberFormat(section.total_dr)}</td>
          <td className="report-amount">{reportNumberFormat(section.total_cr)}</td>
          <td className="report-amount">
            {reportNumberFormat(section.closing_balance)}{section.cr_dr || ''}
          </td>
        </tr>
      </>
    );
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={4} op={4} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell className="other-report-shell--wide">
            <OtherReportPageHeader
              title="Customer Statement"
              subtitle="Ledger activity, opening balance, and closing outstanding by customer"
              badge="Reports"
            />

            <OtherReportFilterCard
              title="Report parameters"
              description="Select one or more customers and a date range, then run the report or export PDF."
            >
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <OtherReportMultiSelectField
                      label="Customers"
                      value={inputValues.customer_ids}
                      placeholder="Search and select customers"
                      span={12}
                      selectAllLabel="Select all"
                      itemLabel="customers"
                      options={customerOptions}
                      onChange={(customer_ids) => setInputValues({ ...inputValues, customer_ids })}
                    />
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                  </>
                )}
                hint={selectedCustomerCount > 0
                  ? `${selectedCustomerCount} customer${selectedCustomerCount === 1 ? '' : 's'} selected`
                  : `${customerOptions.length} customers available`}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
            </OtherReportFilterCard>

            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating customer statement" />}

              {!report && !loading && (
                <OtherReportPlaceholder
                  title="Ready to generate statement"
                  message="Choose customers and dates above, then click Run report. PDF export uses landscape layout so all columns fit."
                />
              )}

              {report && !loading && (
                <OtherReportDocument
                  logoPosition="top"
                  subtitle={report.pump_name || 'Petrosmart'}
                  title="Customer Statement"
                  meta={[
                    report.account_name ? report.account_name : null,
                    formatReportDateRange(report.start_date, report.end_date)
                      ? `Period: ${formatReportDateRange(report.start_date, report.end_date)}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                  rowCount={totalRows}
                  note={sections.length > 1
                    ? `Showing ${sections.length} customer sections in one statement.`
                    : null}
                >
                  {summaryItems.length > 0 ? (
                    <OtherReportSummaryGrid items={summaryItems} />
                  ) : null}

                  {hasLedgerRows ? (
                    <OtherReportTable columns={CUSTOMER_STATEMENT_COLUMNS} className="customer-statement-table">
                      <tbody>
                        {sections.map((section, index) => (
                          <React.Fragment key={`section-${section.customer_id || index}`}>
                            {sections.length > 1 ? (
                              <tr className="other-report-group-row">
                                <td colSpan={CUSTOMER_STATEMENT_COLUMNS.length}>
                                  {section.account_name || `Customer ${index + 1}`}
                                </td>
                              </tr>
                            ) : null}
                            {renderSectionRows(section)}
                            {index < sections.length - 1 ? (
                              <tr className="other-report-section-spacer" aria-hidden="true">
                                <td colSpan={CUSTOMER_STATEMENT_COLUMNS.length} />
                              </tr>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </OtherReportTable>
                  ) : (
                    <OtherReportEmpty
                      message="No ledger activity for the selected customers and dates."
                      hint="Try a wider period or another customer."
                    />
                  )}
                </OtherReportDocument>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default CustomerStatementReport;
